import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Col, Input, Modal, Row, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import { paginationDefaults } from "../../utils/properties";
import { buildQueryString, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";
import CarEngineType from "../../services/api/CarEngineType";

const CarEngineTypes = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirm } = Modal;
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const formikCarEngineType = useFormik({
    initialValues: {
      name: get(state.modal.data, "name", ""),
      description: get(state.modal.data, "description", ""),
      fuel: get(state.modal.data, "fuel", ""),
      measurementDesc: get(state.modal.data, "measurementDesc", ""),
      efficiencyDesc: get(state.modal.data, "efficiencyDesc", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.name) {
        errors.name = "Required!";
      }
      if (!values.description) {
        errors.description = "Required!";
      }
      if (!values.fuel) {
        errors.fuel = "Required!";
      }
      if (!values.measurementDesc) {
        errors.measurementDesc = "Required!";
      }
      if (!values.efficiencyDesc) {
        errors.efficiencyDesc = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await CarEngineType.setCarEngineType(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await CarEngineType.getCarEngineTypes(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikCarEngineType.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await CarEngineType.getCarEngineType(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await CarEngineType.deleteCarEngineType(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("carEngineType.table.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("carEngineType.table.measurementDesc"),
      dataIndex: "measurementDesc",
      key: "measurementDesc",
    },
    {
      title: t("carEngineType.table.efficiencyDesc"),
      dataIndex: "efficiencyDesc",
      key: "efficiencyDesc",
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      name: "",
      measurementDesc: "",
      efficiencyDesc: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      values["page"] = 0;
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "name",
      name: "name",
      value: formikFilter.values.name,
      label: t("carEngineType.table.name"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "measurementDesc",
      name: "measurementDesc",
      value: formikFilter.values.measurementDesc,
      label: t("carEngineType.table.measurementDesc"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("carEngineType.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikCarEngineType.submitForm}
            isDisabled={!(formikCarEngineType.dirty && formikCarEngineType.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="name">
                {t("carEngineType.table.name")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="name"
                name="name"
                onChange={formikCarEngineType.handleChange}
                onBlur={formikCarEngineType.handleBlur}
                value={formikCarEngineType.values.name}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="fuel">
                {t("carEngineType.table.fuel")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="fuel"
                name="fuel"
                onChange={formikCarEngineType.handleChange}
                onBlur={formikCarEngineType.handleBlur}
                value={formikCarEngineType.values.fuel}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="measurementDesc">
                {t("carEngineType.table.measurementDesc")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="measurementDesc"
                name="measurementDesc"
                onChange={formikCarEngineType.handleChange}
                onBlur={formikCarEngineType.handleBlur}
                value={formikCarEngineType.values.measurementDesc}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="description">
                {t("carEngineType.table.description")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="description"
                name="description"
                onChange={formikCarEngineType.handleChange}
                onBlur={formikCarEngineType.handleBlur}
                value={formikCarEngineType.values.description}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={22} md={22} xs={24} sm={22}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="efficiencyDesc">
                {t("carEngineType.table.efficiencyDesc")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="efficiencyDesc"
                name="efficiencyDesc"
                onChange={formikCarEngineType.handleChange}
                onBlur={formikCarEngineType.handleBlur}
                value={formikCarEngineType.values.efficiencyDesc}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default CarEngineTypes;
